import { useSelectorEq } from "commons/store/common";
import type { UiType } from "commons/ui/uiVo";
import { useUiAction } from "commons/ui/useUihook";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { type IState } from "store/modules";
import type { StatisticsTabType } from "./StatisticsVo";
import {
  apiGChannelList,
  apiGStatisticsChartData,
  apiGStatisticsContentGridData,
  apiGStatisticsDateGridData,
  rdxSetStatisticsActiveTab,
  apiGStatisticsPPlist,
  apiGcontextHomeTotal,
  rdxResetDateData,
  rdxResetContentData,
  rdxResetChartData,
} from "./StatisticsR";
import { useApi } from "store/modules/common";
import { useAlert } from "components/popup/popupHook";
import { ActiveType } from "pages/calculate/store/CalculateVo";

/**
 * 통계 > 일별 데이터 분석 api
 * @returns
 */
export const useDateGridListLoad = (tab: StatisticsTabType) => {
  const { apiResult } = useApi();

  return async (num?: number) => {
    const page = num ? num : 0;
    await apiResult(apiGStatisticsDateGridData, { page: page, tab: tab });
  };
};

/**
 * 통계 > 콘텐츠 분석 api
 * @returns
 */
export const useContentGridListLoad = (tab: StatisticsTabType) => {
  const { apiResult } = useApi();

  return async (num?: number) => {
    const page = num ? num : 0;

    await apiResult(apiGStatisticsContentGridData, { page: page, tab: tab });
  };
};

/**
 * 통계 > 차트 데이터 조회
 * @returns
 */
export const useChartListLoad = (tab: StatisticsTabType) => {
  const { apiResult } = useApi();

  return async () => {
    await apiResult(apiGStatisticsChartData, tab);
  };
};

/**
 * 통계 > PP 데이터 리스트
 * @returns
 */
export const usePPListLoad = () => {
  const { apiResult } = useApi();

  return async () => {
    await apiResult(apiGStatisticsPPlist);
  };
};

/**
 * 통계 > 상단 옵션의 채널 선택 셀렉트박스 리스트 조회
 * @returns
 */
export const useChannelListLoad = () => {
  const { apiResult } = useApi();

  return async (type?: StatisticsTabType, subType?: string) => {
    await apiResult(apiGChannelList, { type, subType });
  };
};

export const useIniteDateOption = () => {
  const { add } = useUiAction();

  return (uiType: UiType, id: string, dateOffset: number) => {
    const currentDate = new Date();
    currentDate.setDate(currentDate.getDate() + dateOffset);

    add(uiType, id, moment(currentDate).format("YYYY-MM-DD"));
  };
};

export const useOptionListInit = (tab: StatisticsTabType) => {
  const { optionList, selectChannel } = useSelectorEq((state: IState) => ({
    optionList: state.statistics.channelList,
    selectChannel: state.statistics.selectChannelList,
  }));

  const channelListLoad = useChannelListLoad();

  useEffect(() => {
    channelListLoad(tab);
  }, [tab]);
  return { tab, optionList, selectChannel };
};

/**
 * 통계 > 콘텐츠 / 음원영상 / 파트너스 탭 리스트 관련
 * @returns
 */
export const useTabList = () => {
  const { tab, serviceList } = useSelectorEq((state: IState) => ({
    tab: state.statistics.activeTab,
    serviceList: state.auth.serviceList,
  }));

  let initialList = [
    { id: "PROVIDER", text: "콘텍스트", serviceFlag: false },
    { id: "MEDIA", text: "음원", serviceFlag: false },
  ];

  const activeTab =
    tab !== undefined
      ? tab
      : serviceList !== undefined && serviceList.length > 0
      ? serviceList[0].id === "PARTNERS"
        ? "PROVIDER"
        : serviceList[0].id
      : "PROVIDER";
  const [tabList, setTabList] = useState<{ id: string; text: string; serviceFlag: boolean }[]>([]);
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    if (serviceList && serviceList.length > 0) {
      //서비스 중인 것이 있을때 ...
      if (serviceList.findIndex(list => list.id === ActiveType.PN) > -1) {
        initialList = [...initialList, { id: "PARTNERS", text: "파트너스", serviceFlag: true }];
      }
      const result = initialList.map(tab => {
        const findIndex = serviceList.findIndex(list => list.id === tab.id);
        if (findIndex > -1) {
          return {
            ...tab,
            serviceFlag: true,
          };
        } else {
          return {
            ...tab,
          };
        }
      });
      setTabList(result);
    } else {
      setTabList(initialList);
    }
  }, [serviceList]);

  useEffect(() => {
    if (tabList.length > 0) {
      const serviceFlag = tabList.find(item => item.id === activeTab)?.serviceFlag;
      setIsActive(serviceFlag!);
    } else {
      setIsActive(true);
    }
  }, [activeTab, tabList]);
  return { activeTab, tabList, isActive };
};

/**
 * 통계 일별 데이터 분석 리스트
 * @param tab 상단 탭(콘텐츠 / 음원영상 / 파트너스 / IP)
 * @returns
 */
export const useStatisticsDateGrid = (tab: StatisticsTabType) => {
  const dispatch = useDispatch();

  const { isLoading, date, startDate, endDate, ppSelect, selectChannel } = useSelectorEq((state: IState) => ({
    isLoading: state.statistics.isLoading.date_list,
    date: state.statistics.date,
    startDate: state.ui?.inputText?.[`${tab}_DateOption_start`],
    endDate: state.ui?.inputText?.[`${tab}_DateOption_end`],
    ppSelect: state.statistics.activePpService,
    selectChannel: state.navigates.selectChannel,
  }));
  const dateListLoad = useDateGridListLoad(tab);

  useEffect(() => {
    if (startDate && endDate) {
      dateListLoad();
    }
  }, [tab, startDate, endDate, ppSelect, selectChannel]);

  useEffect(() => {
    return () => {
      dispatch(rdxResetDateData());
    };
  }, []);

  return { isLoading, date };
};

/**
 * 통계 콘텐츠 분석 리스트
 * @param tab 상단 탭(콘텐츠 / 음원영상 / 파트너스 / IP)
 * @returns
 */
export const useStatisticsContentsGrid = (tab: StatisticsTabType) => {
  const dispatch = useDispatch();

  const { isLoading, content, startDate, endDate, ppSelect, selectChannel } = useSelectorEq((state: IState) => ({
    isLoading: state.statistics.isLoading.content_list,
    content: state.statistics.contents,
    startDate: state.ui?.inputText?.[`${tab}_DateOption_start`],
    endDate: state.ui?.inputText?.[`${tab}_DateOption_end`],
    ppSelect: state.statistics.activePpService,
    selectChannel: state.navigates.selectChannel,
  }));
  const contentListLoad = useContentGridListLoad(tab);

  useEffect(() => {
    if (startDate && endDate) {
      contentListLoad();
    }
  }, [tab, startDate, endDate, selectChannel, ppSelect]);

  useEffect(() => {
    return () => {
      dispatch(rdxResetContentData());
    };
  }, []);

  return { isLoading, tab, selectChannel, content };
};

/**
 * 통계 차트 데이터
 * @param tab 상단 탭(콘텐츠 / 음원영상 / 파트너스 / IP)
 * @returns
 */
export const useStatisticsChart = (tab: StatisticsTabType) => {
  const dispatch = useDispatch();

  const { isLoading, chart, startDate, endDate, ppSelect, selectChannel } = useSelectorEq((state: IState) => ({
    isLoading: state.statistics.isLoading.chart,
    chart: state.statistics.chart,
    startDate: state.ui?.inputText?.[`${tab}_DateOption_start`],
    endDate: state.ui?.inputText?.[`${tab}_DateOption_end`],
    ppSelect: state.statistics.activePpService,
    selectChannel: state.navigates.selectChannel,
  }));
  const chartList = useChartListLoad(tab);

  useEffect(() => {
    if (startDate && endDate) {
      chartList();
    }
  }, [tab, startDate, endDate, ppSelect, selectChannel]);

  useEffect(() => {
    return () => {
      dispatch(rdxResetChartData());
    };
  }, []);

  return { isLoading, tab, selectChannel, chart };
};

/**
 * 콘텍스트 > pp list 조회
 * @returns
 */
export const useStatisticsContextChart = (tab: StatisticsTabType) => {
  const { ppData, startDate, endDate, selectChannel, platformCode } = useSelectorEq((state: IState) => ({
    ppData: state.statistics.pp.list,
    startDate: state.ui?.inputText?.[`${tab}_DateOption_start`],
    endDate: state.ui?.inputText?.[`${tab}_DateOption_end`],
    selectChannel: state.navigates.selectChannel,
    platformCode: state.statistics.activePpService,
  }));
  const ppList = usePPListLoad();

  useEffect(() => {
    if (startDate && endDate) {
      ppList();
    }
  }, [tab, startDate, endDate, selectChannel]);

  return { ppData, platformCode };
};
